import { useMemo } from 'react';
import {
  type RouteObject,
  RouterProvider,
  createBrowserRouter
} from 'react-router-dom';
import { BaseLayout } from '@/app/layouts/base-layout.tsx';
import { MainErrorFallback } from '@/app/components/errors/main-error.tsx';
import { Protected } from '@/app/routes/auth/protected.tsx';
import { DocumentsProvider } from '@/app/DocumentsProvider.tsx';
import { AUTH } from '@/app/consts/auth.ts';
import { AuthorizedRoute } from '@/app/lib/auth/authorized.tsx';
import { LOCATION } from '@/app/consts/location.ts';

const createAppRouter = () =>
  createBrowserRouter([
    {
      element: <BaseLayout />,
      ErrorBoundary: MainErrorFallback,
      children: [
        {
          path: LOCATION.LOGIN,
          lazy: async () => {
            const { LoginRoute } = await import('./auth/loginRoute.tsx');
            return { Component: LoginRoute } satisfies RouteObject;
          },
          ErrorBoundary: MainErrorFallback
        },
        {
          path: LOCATION.LOGOUT,
          lazy: async () => {
            const { LogoutRoute } = await import('./auth/logout.tsx');
            return { Component: LogoutRoute } satisfies RouteObject;
          },
          ErrorBoundary: MainErrorFallback
        },
        {
          path: LOCATION.TERMS_OF_USE,
          lazy: async () => {
            const { TermsOfUse } = await import('./pages/terms-of-use.tsx');
            return { Component: TermsOfUse } satisfies RouteObject;
          },
          ErrorBoundary: MainErrorFallback
        },
        {
          path: LOCATION.PRIVACY_POLICY,
          lazy: async () => {
            const { PrivacyCookiesPolicy } = await import(
              './pages/privacy-cookies-policy.tsx'
            );
            return { Component: PrivacyCookiesPolicy } satisfies RouteObject;
          },
          ErrorBoundary: MainErrorFallback
        },
        {
          lazy: async () => {
            const { AppRoot } = await import('./root.tsx');
            return {
              element: (
                <Protected>
                  <AuthorizedRoute allowedGroups={AUTH.APP}>
                    <DocumentsProvider>
                      <AppRoot />
                    </DocumentsProvider>
                  </AuthorizedRoute>
                </Protected>
              )
            } satisfies RouteObject;
          },
          ErrorBoundary: MainErrorFallback,
          children: [
            {
              path: LOCATION.ROOT,
              lazy: async () => {
                const { DocumentOverview } = await import(
                  './pages/document-overview.tsx'
                );
                return {
                  element: <DocumentOverview />
                } satisfies RouteObject;
              },
              ErrorBoundary: MainErrorFallback
            },
            {
              path: LOCATION.DOCUMENT_DETAILS,
              lazy: async () => {
                const { DocumentDetails } = await import(
                  './pages/document-details.tsx'
                );
                return {
                  element: <DocumentDetails />
                } satisfies RouteObject;
              },
              ErrorBoundary: MainErrorFallback
            },
            {
              path: LOCATION.PROFILE,
              lazy: async () => {
                const { Profile } = await import('./pages/profile.tsx');
                return {
                  element: <Profile />
                } satisfies RouteObject;
              },
              ErrorBoundary: MainErrorFallback
            },
            {
              path: LOCATION.UNITS,
              lazy: async () => {
                const { UnitsDetails } = await import(
                  './pages/units-details.tsx'
                );
                return {
                  element: <UnitsDetails />
                } satisfies RouteObject;
              },
              ErrorBoundary: MainErrorFallback
            }
          ]
        },
        {
          path: LOCATION.FORBIDDEN,
          lazy: async () => {
            const { ForbiddenPage } = await import('./pages/forbidden.tsx');
            return {
              element: <ForbiddenPage />
            } satisfies RouteObject;
          },
          ErrorBoundary: MainErrorFallback
        }
      ]
    }
  ]);

export function AppRouter() {
  const router = useMemo(() => createAppRouter(), []);

  return <RouterProvider router={router} />;
}
